import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./App";
import { Radio, DollarSign, CheckCircle, Clock, Calendar, Award, ChevronDown, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Aly Transmission Portal
 * Self-service production + payment view. Aly (role=National Transmission)
 * sees only his own transmitted orders, grouped by pay period.
 */
export default function AlyPortalPage({ Layout }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [rate, setRate] = useState(12.0);
  const [loading, setLoading] = useState(true);
  const [openedPeriods, setOpenedPeriods] = useState(new Set());
  const [toast, setToast] = useState(null);
  const [rateEdit, setRateEdit] = useState("");
  const [savingRate, setSavingRate] = useState(false);

  const isAdmin = user?.role === "Admin" || user?.role === "Finance";

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/aly/my-transmissions`);
      setData(r.data);
      setRate(r.data?.summary?.rate_brl_per_order || 12.0);
      // Auto-open the two most recent periods so they're visible on load.
      if (r.data?.periods?.length > 0) {
        setOpenedPeriods(new Set(r.data.periods.slice(0, 2).map(p => p.period_key)));
      }
    } catch (e) {
      setToast({ type: "error", text: e.response?.data?.detail || "Failed to load transmissions" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const togglePeriod = (key) => {
    setOpenedPeriods(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const markPaid = async (periodKey, periodLabel) => {
    if (!isAdmin) return;
    if (!window.confirm(`Mark all pending qualifying orders in "${periodLabel}" as paid to Aly?`)) return;
    try {
      const r = await axios.post(`${API}/aly/mark-paid`, { period_key: periodKey });
      setToast({ type: "success", text: `Payment recorded: R$${r.data.payment.total.toFixed(2)} (${r.data.payment.order_ids.length} orders).` });
      fetchData();
    } catch (e) {
      setToast({ type: "error", text: e.response?.data?.detail || "Failed to mark paid" });
    }
  };

  const saveRate = async () => {
    const v = parseFloat(rateEdit);
    if (!v || v <= 0) return;
    setSavingRate(true);
    try {
      await axios.post(`${API}/aly/settings/rate`, { rate_brl_per_order: v });
      setToast({ type: "success", text: `Rate updated to R$${v.toFixed(2)} per qualifying order.` });
      setRateEdit("");
      fetchData();
    } catch (e) {
      setToast({ type: "error", text: e.response?.data?.detail || "Failed to save rate" });
    } finally {
      setSavingRate(false);
    }
  };

  const s = data?.summary;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="bg-[#6B5B8D]/15 rounded-lg p-2">
            <Radio className="h-5 w-5 text-[#6B5B8D]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#1F2937]">Transmission Portal</h1>
            <p className="text-sm text-gray-500">{user?.name || "Aly"} — National Internal transmissions</p>
          </div>
        </div>

        {toast && (
          <div className={`mb-4 px-3 py-2 rounded-md text-sm ${toast.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
            {toast.text}
            <button onClick={() => setToast(null)} className="float-right opacity-60 hover:opacity-100">×</button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-10 text-gray-400">Loading…</div>
        ) : !data ? (
          <div className="text-center py-10 text-gray-400">No data.</div>
        ) : (
          <>
            {/* SUMMARY TILES */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="aly-tile-transmitted">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">Total Transmitted</p>
                <p className="text-3xl font-bold text-[#1F2937] mt-1">{s.total_transmitted}</p>
                <p className="text-[10px] text-gray-500 mt-1">orders all-time</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="aly-tile-qualifying">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">Qualifying</p>
                <p className="text-3xl font-bold text-[#6B5B8D] mt-1">{s.total_qualifying}</p>
                <p className="text-[10px] text-gray-500 mt-1">National Internal</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="aly-tile-paid">
                <p className="text-[10px] text-green-700 uppercase tracking-wider font-bold">Paid</p>
                <p className="text-3xl font-bold text-green-700 mt-1">R${s.total_paid_brl.toFixed(2)}</p>
                <p className="text-[10px] text-green-700 mt-1">{s.total_paid_brl > 0 ? "received" : "—"}</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="aly-tile-pending">
                <p className="text-[10px] text-amber-700 uppercase tracking-wider font-bold">Pending</p>
                <p className="text-3xl font-bold text-amber-700 mt-1">R${s.total_pending_brl.toFixed(2)}</p>
                <p className="text-[10px] text-amber-700 mt-1">to be paid</p>
              </div>
            </div>

            {/* RATE + ADMIN SETTINGS STRIP */}
            <div className="bg-[#F5F9FF] border border-[#9FB6D6]/40 rounded-lg p-3 mb-5 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2 text-sm">
                <DollarSign className="h-4 w-4 text-[#2F5D8A]" />
                <span className="text-gray-600">Current rate per qualifying order:</span>
                <b className="text-[#2F5D8A]" data-testid="aly-current-rate">R${rate.toFixed(2)}</b>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="0.50"
                    placeholder="New rate"
                    value={rateEdit}
                    onChange={e => setRateEdit(e.target.value)}
                    className="w-24 px-2 py-1 text-sm border border-gray-300 rounded"
                    data-testid="aly-rate-input"
                  />
                  <button
                    onClick={saveRate}
                    disabled={savingRate || !rateEdit}
                    className="px-3 py-1 bg-[#2F5D8A] hover:bg-[#24496e] text-white text-xs font-semibold rounded disabled:opacity-40"
                    data-testid="aly-rate-save"
                  >
                    {savingRate ? "Saving…" : "Update rate"}
                  </button>
                </div>
              )}
            </div>

            {/* PERIODS */}
            <div className="space-y-3">
              {data.periods.length === 0 && (
                <p className="text-center text-gray-400 py-10">No transmissions yet.</p>
              )}
              {data.periods.map(p => {
                const open = openedPeriods.has(p.period_key);
                const fullyPaid = p.paid_brl >= p.total_qualifying_brl && p.total_qualifying_brl > 0;
                return (
                  <div key={p.period_key} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`period-${p.period_key}`}>
                    <button
                      onClick={() => togglePeriod(p.period_key)}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        {open ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                        <div className="text-left">
                          <p className="text-sm font-bold text-[#1F2937]">{p.period_label}</p>
                          <p className="text-[11px] text-gray-500">
                            <Calendar className="h-3 w-3 inline mr-1" /> Pay Friday: {p.pay_friday}
                            {p.qualifying_count > 0 && <> · {p.qualifying_count} qualifying</>}
                            {p.non_qualifying_count > 0 && <> · <span className="text-gray-400">{p.non_qualifying_count} non-qualifying</span></>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {fullyPaid && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-800 text-[10px] font-bold rounded-full uppercase tracking-wide flex items-center gap-1" data-testid={`period-paid-${p.period_key}`}>
                            <CheckCircle className="h-3 w-3" /> Paid
                          </span>
                        )}
                        {!fullyPaid && p.pending_brl > 0 && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-bold rounded-full uppercase tracking-wide">
                            Pending R${p.pending_brl.toFixed(2)}
                          </span>
                        )}
                        <div className="text-right">
                          <p className="text-base font-bold text-[#6B5B8D]">R${p.total_qualifying_brl.toFixed(2)}</p>
                          <p className="text-[10px] text-gray-400">{p.orders.length} order{p.orders.length !== 1 ? "s" : ""}</p>
                        </div>
                      </div>
                    </button>
                    {open && (
                      <div className="border-t border-gray-100">
                        {p.orders.length === 0 ? (
                          <p className="text-center text-gray-400 text-sm py-6">No orders in this period yet.</p>
                        ) : (
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-100">
                                <th className="px-3 py-2 text-left font-semibold text-gray-400 uppercase text-[10px]">Order</th>
                                <th className="px-3 py-2 text-left font-semibold text-gray-400 uppercase text-[10px]">Inspector</th>
                                <th className="px-3 py-2 text-left font-semibold text-gray-400 uppercase text-[10px]">Company</th>
                                <th className="px-3 py-2 text-left font-semibold text-gray-400 uppercase text-[10px]">Type</th>
                                <th className="px-3 py-2 text-left font-semibold text-gray-400 uppercase text-[10px]">Trans. Date</th>
                                <th className="px-3 py-2 text-right font-semibold text-gray-400 uppercase text-[10px]">Value</th>
                                <th className="px-3 py-2 text-center font-semibold text-gray-400 uppercase text-[10px]">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {p.orders.map((o, i) => (
                                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`period-row-${p.period_key}-${o.order_number}`}>
                                  <td className="px-3 py-2 font-semibold text-[#2F5D8A]">{o.order_number}</td>
                                  <td className="px-3 py-2 text-gray-600">{o.inspector}</td>
                                  <td className="px-3 py-2 text-gray-600">{o.company}</td>
                                  <td className="px-3 py-2">
                                    {o.qualifies_for_rate ? (
                                      <span className="text-[#6B5B8D] font-semibold">{o.type}</span>
                                    ) : (
                                      <span className="text-gray-400">{o.type}</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-gray-500 font-mono">{o.transmission_date}</td>
                                  <td className="px-3 py-2 text-right font-semibold">
                                    {o.qualifies_for_rate ? `R$${o.payment_value_brl.toFixed(2)}` : <span className="text-gray-400">—</span>}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    {o.payment_status === "paid" ? (
                                      <span className="px-1.5 py-0.5 bg-green-100 text-green-800 text-[9px] font-bold rounded uppercase">Paid</span>
                                    ) : o.qualifies_for_rate ? (
                                      <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[9px] font-bold rounded uppercase">Pending</span>
                                    ) : (
                                      <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 text-[9px] font-bold rounded uppercase">N/A</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}

                        {/* Payment history for this period */}
                        {p.payments.length > 0 && (
                          <div className="bg-green-50/30 border-t border-green-100 px-4 py-2.5">
                            <p className="text-[10px] font-bold text-green-800 uppercase tracking-wider mb-1">Payment History</p>
                            {p.payments.map((pay, i) => (
                              <div key={i} className="text-[11px] text-gray-700 flex items-center justify-between py-0.5" data-testid={`period-payment-${p.period_key}-${i}`}>
                                <span>
                                  <Award className="h-3 w-3 inline mr-1 text-green-700" />
                                  Paid on <b>{(pay.paid_at || "").slice(0, 10)}</b> · {pay.order_ids?.length || 0} orders
                                  {pay.created_by_name && <> · by {pay.created_by_name}</>}
                                </span>
                                <b className="text-green-800">R${Number(pay.total || 0).toFixed(2)}</b>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Admin action: mark period as paid */}
                        {isAdmin && p.pending_brl > 0 && (
                          <div className="border-t border-gray-100 px-4 py-2.5 flex justify-end">
                            <button
                              onClick={() => markPaid(p.period_key, p.period_label)}
                              className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5"
                              data-testid={`mark-paid-${p.period_key}`}
                            >
                              <CheckCircle className="h-3.5 w-3.5" /> Mark Period Paid · R${p.pending_brl.toFixed(2)}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
